// frontend/src/utils/notifications.ts
import api from '../services/api';

// ─────────────────────────────────────────────
// LOCAL NOTIFICATION TYPES (client-side)
// ─────────────────────────────────────────────
export type LocalNotificationType =
  | 'review_posted'
  | 'review_updated'
  | 'comment_posted'
  | 'notebook_logged';

export interface LocalNotification {
  id: string;
  type: LocalNotificationType;
  message: string;
  movieId?: number;
  movieTitle?: string;
  createdAt: string;
  isRead: boolean;
}

const LOCAL_NOTIF_KEY = 'romcom_local_notifications';

const generateId = () => `local_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// ── Read from localStorage ──
export const getLocalNotifications = (): LocalNotification[] => {
  try {
    const raw = localStorage.getItem(LOCAL_NOTIF_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

// ── Save to localStorage ──
const saveLocalNotifications = (notifs: LocalNotification[]) => {
  // Keep max 50 newest
  const trimmed = notifs.slice(0, 50);
  localStorage.setItem(LOCAL_NOTIF_KEY, JSON.stringify(trimmed));
};

// ── Add a new local notification ──
export const addNotification = (payload: {
  type: LocalNotificationType;
  message: string;
  movieId?: number;
  movieTitle?: string;
}): LocalNotification => {
  const notif: LocalNotification = {
    id: generateId(),
    ...payload,
    createdAt: new Date().toISOString(),
    isRead: false,
  };
  const existing = getLocalNotifications();
  saveLocalNotifications([notif, ...existing]);
  return notif;
};

// ── Mark one as read ──
export const markLocalNotificationRead = (id: string) => {
  const notifs = getLocalNotifications().map(n =>
    n.id === id ? { ...n, isRead: true } : n
  );
  saveLocalNotifications(notifs);
};

// ── Mark all as read ──
export const markAllLocalNotificationsRead = () => {
  const notifs = getLocalNotifications().map(n => ({ ...n, isRead: true }));
  saveLocalNotifications(notifs);
};

// ── Delete one ──
export const deleteLocalNotification = (id: string) => {
  const notifs = getLocalNotifications().filter(n => n.id !== id);
  saveLocalNotifications(notifs);
};

// ── Unread count ──
export const getLocalUnreadCount = (): number =>
  getLocalNotifications().filter(n => !n.isRead).length;

// ─────────────────────────────────────────────
// SERVER NOTIFICATION HELPERS (existing API)
// ─────────────────────────────────────────────
export const getNotifications = async () => {
  try {
    const res = await api.get('/notifications');
    return res.data.notifications || [];
  } catch {
    return [];
  }
};

export const getUnreadCount = async () => {
  try {
    const res = await api.get('/notifications/unread-count');
    return res.data.count || 0;
  } catch {
    return 0;
  }
};

export const markNotificationsRead = async () => {
  try {
    await api.patch('/notifications/read-all');
  } catch {
    // silently fail
  }
};

export const markOneRead = async (id: string) => {
  try {
    await api.patch(`/notifications/${id}/read`);
  } catch {
    // silently fail
  }
};